import { hexToRgb, hexToOklch, oklchToHex } from './oklch.js'

/** WCAG 2.1 relative luminance. */
function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex)
  const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/** Tỉ lệ tương phản WCAG, 1..21. AA cho body text là 4.5. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a)
  const lb = luminance(b)
  const [hi, lo] = la > lb ? [la, lb] : [lb, la]
  return (hi + 0.05) / (lo + 0.05)
}

export const AA_NORMAL = 4.5
export const AA_LARGE = 3

/**
 * Chọn màu chữ đặt trên nền `bg`: trắng hay gần-đen, cái nào tương phản hơn.
 */
export function pickTextOn(bg: string, light = '#FFFFFF', dark = '#111827'): string {
  return contrastRatio(bg, light) >= contrastRatio(bg, dark) ? light : dark
}

/**
 * Guard: đảm bảo nút primary luôn đọc được.
 *
 * Vì sao cần: partner gửi đúng một màu brand. Vàng #FFD400 và tím #7C5CFF đều hợp lệ,
 * nhưng chữ trắng trên vàng có contrast ~1.3 — không đọc được. Không thể bắt Ops tự biết
 * điều đó, cũng không thể để mỗi partner tự khai màu chữ (họ sẽ khai sai).
 *
 * Cách xử lý: giữ nguyên màu brand cho phần trang trí, và tối/sáng dần MỘT màu dẫn xuất
 * (`primary`) tới khi đạt AA. Giữ nguyên hue và chroma nên vẫn ra "đúng màu partner".
 */
export function ensureAccessible(
  brandHex: string,
  target = AA_NORMAL,
): { primary: string; onPrimary: string; adjusted: boolean } {
  // Nút bấm & thành phần giao diện lớn: ưu tiên chữ trắng nếu đạt tỉ lệ tương phản >= 3:1 (AA Large)
  const whiteRatio = contrastRatio(brandHex, '#FFFFFF')
  if (whiteRatio >= AA_LARGE) {
    return { primary: brandHex, onPrimary: '#FFFFFF', adjusted: false }
  }

  const onPrimary = pickTextOn(brandHex)
  if (contrastRatio(brandHex, onPrimary) >= target) {
    return { primary: brandHex, onPrimary, adjusted: false }
  }

  // Chữ trắng dễ hợp với nút bấm hơn → ưu tiên làm tối brand để trắng đọc được.
  const base = hexToOklch(brandHex)
  let lo = 0
  let hi = base.l
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2
    if (contrastRatio(oklchToHex({ ...base, l: mid }), '#FFFFFF') >= target) lo = mid
    else hi = mid
  }
  const darkened = oklchToHex({ ...base, l: lo })

  // Màu quá sáng (vàng chanh) có thể phải tối tới mức mất hết đặc trưng — khi đó dùng chữ đen.
  if (contrastRatio(darkened, '#FFFFFF') < target) {
    return { primary: brandHex, onPrimary: '#111827', adjusted: true }
  }
  return { primary: darkened, onPrimary: '#FFFFFF', adjusted: true }
}
